import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Phone, MessageCircle, Mail, MapPin, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { PHONE_DISPLAY, PHONE_TEL, COMPANY_EMAIL, whatsappLink } from "../data/constants";
import api from "../lib/api";

export default function ContactPage() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", business: "", phone: "", email: "", product: "", colour: "", size: "", quantity: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please enter your name and phone"); return; }
    setLoading(true);
    try {
      await api.post("/enquiries", { ...form, source: "contact_page" });
      toast.success("Enquiry sent! We'll get back to you soon.");
      setForm({ name: "", business: "", phone: "", email: "", product: "", colour: "", size: "", quantity: "", message: "" });
    } catch { toast.error("Could not send. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0B1B3D] sm:text-4xl">Contact & Enquiry</h1>
          <p className="mt-2 text-slate-600">Send us your requirement or reach out directly — we respond fast.</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-1">
            {[
              { icon: Phone, label: "Call us", value: PHONE_DISPLAY, href: `tel:${PHONE_TEL}` },
              { icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: whatsappLink() },
              { icon: Mail, label: "Email", value: COMPANY_EMAIL, href: `mailto:${COMPANY_EMAIL}` },
            ].map((c) => (
              <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-[#10B981] hover:shadow-lg">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#ECFDF5] text-[#059669]"><c.icon className="h-6 w-6" /></span>
                <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{c.label}</p><p className="font-display font-semibold text-[#0B1B3D] break-all">{c.value}</p></div>
              </a>
            ))}
            <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#ECFDF5] text-[#059669]"><MapPin className="h-6 w-6" /></span>
              <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Location</p><p className="font-display font-semibold text-[#0B1B3D]">Guwahati, Assam, India</p></div>
            </div>
          </div>

          <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:col-span-2" data-testid="contact-enquiry-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Name *</Label><Input data-testid="contact-input-name" value={form.name} onChange={set("name")} placeholder="Your name" /></div>
              <div><Label>Business</Label><Input data-testid="contact-input-business" value={form.business} onChange={set("business")} placeholder="Company / shop" /></div>
              <div><Label>Phone *</Label><Input data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} placeholder="Phone" /></div>
              <div><Label>Email</Label><Input data-testid="contact-input-email" type="email" value={form.email} onChange={set("email")} placeholder="For a confirmation reply" /></div>
              <div>
                <Label>Product</Label>
                <Select value={form.product} onValueChange={(v) => setForm((f) => ({ ...f, product: v }))}>
                  <SelectTrigger data-testid="contact-select-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {products.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Colour</Label><Input data-testid="contact-input-colour" value={form.colour} onChange={set("colour")} placeholder="Preferred colour" /></div>
              <div><Label>Size</Label><Input data-testid="contact-input-size" value={form.size} onChange={set("size")} placeholder="Preferred size" /></div>
              <div><Label>Quantity</Label><Input data-testid="contact-input-quantity" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 100 pcs" /></div>
            </div>
            <div className="mt-4"><Label>Message</Label><Textarea data-testid="contact-input-message" value={form.message} onChange={set("message")} placeholder="How can we help?" rows={4} /></div>
            <Button type="submit" disabled={loading} data-testid="contact-submit-button" className="mt-5 bg-[#10B981] font-bold hover:bg-[#059669]">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send Enquiry
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
